<template>
  <div class="online-order-page">
    <block-buttons
      title="Интернет-заказы"
      description="Управляйте заказами"
      :actions="toolbarActions"
    >
      <template #icon>
        <ShoppingBag :size="22" />
      </template>
    </block-buttons>

    <data-table
      :rows="renderData"
      :columns="tableColumns"
      :loading="loading"
      :error="error"
      title="Интернет-заказы"
      description="Просмотр и обработка интернет-заказов"
      search-placeholder="Поиск по заказам"
      empty-text="Интернет-заказов пока нет"
      @retry="loadDataWrapper(true)"
    >
      <template #header>
        <span class="online-order-page__count">Заказов: {{ getData.length }}</span>
      </template>

      <template #cell-title="{ row }">
        <strong>{{ row.title }}</strong>
      </template>

      <template #cell-status="{ row }">
        <span class="online-order-page__count">{{ row.status }}</span>
      </template>

      <template #cell-actions="{ row }">
        <select
          :disabled="loading"
          :aria-label="`Действия с заказом ${row.title}`"
          @change="selectAction($event, row)"
        >
          <option value="">Действия</option>

          <option
            v-for="action in rowActions"
            :key="action.id"
            :value="action.id"
          >
            {{ action.label }}
          </option>
        </select>
      </template>
    </data-table>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import {
  ShoppingBag,
  Plus,
  RefreshCw
} from 'lucide-vue'

import BlockButtons from '@/components/blockButtons'
import DataTable from '@/components/dataTable'
import permissions from '@/mixins/permissions'

export default {
  name: 'OnlineOrderV2',
  components: {
    ShoppingBag,
    BlockButtons,
    DataTable
  },
  mixins: [permissions],
  data () {
    return {
      loading: false,
      error: ''
    }
  },
  computed: {
    ...mapGetters('onlineOrder', ['getData']),
    ...mapGetters('settings', ['getTableColumns', 'getDefaultValues']),
    renderData () {
      return this.getData.map(order => ({
        ...order,
        status: this.getDefaultValues?.['online-order-statuses']?.find(({ code }) => code === order.status)?.name || order.status || '—'
      }))
    },
    toolbarActions () {
      const actions = [
        {
          id: 'refresh',
          label: 'Обновить',
          icon: RefreshCw,
          disabled: this.loading,
          handler: this.loadDataWrapper
        }
      ]

      if (this.isAllowedOperation('create', 'onlineOrder')) {
        actions.push({
          id: 'create',
          label: 'Новый онлайн заказ',
          icon: Plus,
          primary: true,
          disabled: this.loading,
          handler: () => {
            this.openModal({
              name: 'create-base',
              propsData: {
                name: 'online-order',
                moduleAction: 'onlineOrder',
                type: 'create',
                title: 'Добавление нового онлайн заказа',
                text: 'Укажите данные онлайн заказа',
                textCancelBtn: 'Закрыть',
                textConfirmBtn: 'Добавить категорию'
              }
            })
          }
        })
      }

      return actions
    },
    tableColumns () {
      return [
        {
          key: 'actions',
          label: 'Действия',
          sortable: false
        },
        ...(this.getTableColumns?.['online-order'] || [])
      ]
    },
    rowActions () {
      return [
        {
          id: 'info',
          label: 'Информация',
          visible: true
        },
        {
          id: 'change',
          label: 'Редактировать',
          visible: this.isAllowedOperation('change', 'onlineOrder')
        },
        {
          id: 'remove',
          label: 'Удалить',
          visible: this.isAllowedOperation('remove', 'onlineOrder')
        }
      ].filter(({ visible }) => visible)
    }
  },
  mounted () {
    this.loadDataWrapper()
  },
  methods: {
    ...mapActions('onlineOrder', ['loadData', 'remove']),
    ...mapActions('modal', ['openModal']),
    async loadDataWrapper () {
      if (this.loading) {
        return
      }

      this.$bus.emit('start-loader')
      this.loading = true

      await this.loadData()

      this.loading = false
      this.$bus.emit('stop-loader')
    },
    selectAction (event, row) {
      const action = event.target.value

      event.target.value = ''

      if (!action) {
        return
      }

      this.openAction(action, row)
    },
    openAction (action, row) {
      switch (action) {
        case 'info':
          this.openModal({
            name: 'info-base',
            propsData: {
              name: 'online-order',
              moduleAction: 'onlineOrder',
              type: 'info',
              title: `Информация - ${row.title}`,
              textCancelBtn: 'Закрыть',
              data: row
            }
          })

          break
        case 'change':
          this.openModal({
            name: 'change-base',
            propsData: {
              name: 'online-order',
              moduleAction: 'onlineOrder',
              type: 'change',
              title: `Редактирование - ${row.title}`,
              text: 'Измените необходимые данные',
              textCancelBtn: 'Закрыть',
              textConfirmBtn: 'Сохранить',
              data: row
            }
          })

          break
        case 'remove':
          this.removeOnlineCategory(row)
      }
    },
    async removeOnlineCategory ({ id }) {
      const { error } = await this.remove(id)

      if (error) {
        this.$notify({
          type: 'error',
          title: 'Ошибка при удалении!',
          text: 'Произошла ошибка при удалении онлайн заказа!',
          clean: false,
          duration: 3000
        })

        return
      }

      this.$notify({
        type: 'success',
        title: 'Онлайн успешно удалена!',
        text: '',
        clean: false,
        duration: 3000
      })
    }
  }
}
</script>

<style scoped lang="scss" src="./style.scss" />
